<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import PainelAPI from 'dashboard/api/painel';

const props = defineProps({ open: Boolean });
const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();

const records = ref([]);
const loading = ref(false);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await PainelAPI.fetchAppointments({ archived: true });
    records.value = (data?.groups || []).flatMap(g => g.appointments);
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.open,
  o => {
    if (o) load();
  }
);

const restore = async appt => {
  await store.dispatch('painelAppointments/restore', appt.id);
  records.value = records.value.filter(a => a.id !== appt.id);
};
</script>

<template>
  <div>
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="emit('close')"
    >
      <div class="w-full max-w-2xl rounded-lg bg-n-solid-1 p-6 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-lg font-semibold">
            {{ t('PAINEL.ARCHIVED.TITLE') }}
          </h3>
          <button
            :aria-label="t('PAINEL.APPOINTMENTS.DETAIL.CLOSE')"
            class="text-n-slate-10"
            @click="emit('close')"
          />
        </div>

        <div v-if="loading" class="py-8 text-center text-n-slate-10">
          {{ t('PAINEL.ARCHIVED.LOADING') }}
        </div>
        <div
          v-else-if="!records.length"
          class="py-8 text-center text-n-slate-10"
        >
          {{ t('PAINEL.ARCHIVED.EMPTY') }}
        </div>
        <ul v-else class="divide-y divide-n-slate-5">
          <li
            v-for="a in records"
            :key="a.id"
            class="flex items-center gap-3 py-3"
          >
            <div class="flex-1">
              <div class="font-medium">
                {{ a.title || t('PAINEL.ARCHIVED.UNTITLED') }}
              </div>
              <div class="text-xs text-n-slate-10">{{ a.starts_at }}</div>
            </div>
            <button
              class="rounded-md border border-n-slate-5 px-3 py-1.5 text-sm hover:bg-n-slate-2"
              @click="restore(a)"
            >
              {{ t('PAINEL.ARCHIVED.RESTORE') }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
